// Emoji-safe text helpers (KEY-757).
//
// People put emoji in section, action point and item names ("🎂 Birthdays", "👨‍👩‍👧 Family", "🇬🇧 Trips"). A JS string
// counts UTF-16 code units, and an emoji is two or more of them: 🎂 is a surrogate pair, 👨‍👩‍👧 is five code points
// joined by zero-width joiners, a flag is two regional indicators. So `label.length` over-counts and
// `label.slice(0, n)` can cut one in half — a lone surrogate renders as "�" (and is invalid XML in an exported SVG),
// half a ZWJ family shows the wrong people. Everything that shortens or measures a label goes through these instead.
//
// Grapheme clusters (what a reader sees as one character) come from Intl.Segmenter where the runtime has it; the
// fallback is code points, which never splits a surrogate pair but may split a ZWJ sequence or a flag.

type SegmenterLike = { segment(input: string): Iterable<{ segment: string }> };

let segmenter: SegmenterLike | null | undefined;

function getSegmenter(): SegmenterLike | null {
  if (segmenter === undefined) {
    const Ctor = (Intl as unknown as { Segmenter?: new (locale?: string, options?: { granularity: string }) => SegmenterLike }).Segmenter;
    segmenter = typeof Ctor === "function" ? new Ctor(undefined, { granularity: "grapheme" }) : null;
  }
  return segmenter;
}

/** The string split into grapheme clusters (code points where Intl.Segmenter is unavailable). */
export function graphemes(text: string): string[] {
  if (!text) return [];
  const seg = getSegmenter();
  return seg ? Array.from(seg.segment(text), (s) => s.segment) : Array.from(text);
}

/** How many characters a reader sees: "🎂" is 1, "👨‍👩‍👧" is 1, "AB" is 2. */
export function graphemeCount(text: string): number {
  return graphemes(text).length;
}

/** The first `max` visible characters, never half of one. */
export function truncateGraphemes(text: string, max: number): string {
  if (max <= 0 || !text) return "";
  // Fast path: a string no longer than `max` code units can't hold more than `max` graphemes.
  if (text.length <= max) return text;
  const parts = graphemes(text);
  return parts.length <= max ? text : parts.slice(0, max).join("");
}

/** The first visible character — a letter, or a whole emoji. Empty for an empty string. */
export function firstGrapheme(text: string): string {
  return graphemes(text)[0] ?? "";
}

// Extended pictographics and regional indicators draw about one em wide; Latin text averages ~0.6em. Used only to
// estimate how much of a label fits an arc, so a near-miss is harmless.
const WIDE = /[\p{Extended_Pictographic}\p{Regional_Indicator}]/u;

/** Rough rendered width of `text` in ems: ~0.6 per ordinary character, ~1 per emoji. */
export function estimateTextEms(text: string): number {
  let ems = 0;
  for (const g of graphemes(text)) ems += WIDE.test(g) ? 1 : 0.6;
  return ems;
}

// Sums of 0.6 drift in floating point (3 × 0.6 = 1.7999…); without slack a label that exactly fits would be cut.
const EPS = 1e-9;

/**
 * Fit `text` into `maxEms` of width: the whole thing, else as many whole characters as fit plus "…", else "" when
 * not even one character and the ellipsis would.
 */
export function fitTextToEms(text: string, maxEms: number): string {
  if (estimateTextEms(text) <= maxEms + EPS) return text;
  const ellipsis = 0.6;
  let used = 0;
  let out = "";
  for (const g of graphemes(text)) {
    const w = WIDE.test(g) ? 1 : 0.6;
    if (used + w + ellipsis > maxEms + EPS) break;
    used += w;
    out += g;
  }
  out = out.trimEnd();
  return out ? `${out}…` : "";
}

/**
 * The string with any unpaired surrogate replaced by U+FFFD, so it is well-formed UTF-16 — a lone surrogate is not a
 * legal XML character, and one in an exported SVG makes the whole document unparseable.
 */
export function toWellFormed(text: string): string {
  return text.replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "�");
}
